import * as React from 'react'
import {Component, FC} from 'react'
import {observable, toJS} from 'mobx'
import {observer} from 'mobx-react'
import {keys, values} from 'lodash'
import {sprintf} from 'sprintf-js'
import {Transition} from 'react-transition-group'
import constate from 'constate'
import {PaymentFlow} from '@raisenow/epik'
import {useStores} from '../../lib/MobXProvider'
import {WidgetApi} from '../../lib/WidgetApi'
import {Block} from '../../lib/Block'
import {WidgetFlag} from '../../lib/WidgetFlag'
import {OverlayMode} from '../../lib/OverlayMode'
import {buildSlideTransitionProps} from '../../lib/helpers'

///////////////////////////////////////////////////////////////////////////////

type DebugState = {
  statement: string
  statementResult: string
  toggles: string[]
  filterConfigNullValues: boolean
  showAllTranslations: boolean
  showTranslationsFallbacks: boolean
  hideComputations: boolean
  hideNonComputations: boolean
}

///////////////////////////////////////////////////////////////////////////////

export class Debug extends Component {
  @observable debugState: DebugState

  constructor(props) {
    super(props)

    this.debugState = {
      statement: '',
      statementResult: '',
      toggles: [],
      filterConfigNullValues: true,
      showAllTranslations: false,
      showTranslationsFallbacks: false,
      hideComputations: false,
      hideNonComputations: false,
    }
  }

  render() {
    return (
      <DebugStateProvider debugState={this.debugState}>
        <div className="debug">
          <div className="title">Debug</div>
          <DebugToggles />
          <DebugFlags />
          <DebugPaymentForm />
          <DebugSubscriptionRenewForm />
          <DebugTransactionInfo />
          <DebugSubscriptionInfo />
          <DebugCustomerInfo />
          <DebugEpmsPaymentInfo />
          <DebugEpmsSubscriptionInfo />
          <DebugEpmsPaymentSourceInfo />
          <DebugEpmsPaymentRequestResponseData />
          <DebugConfig />
          <DebugTranslations />
          <DebugBlocks />
          <DebugConditions />
        </div>
      </DebugStateProvider>
    )
  }
}

////////////////////////////////////////////////////////////////////////////////

export type DebugStateProviderProps = {
  debugState: DebugState
}

export const [DebugStateProvider, useDebugState] = constate(
  (props: DebugStateProviderProps) => {
    const {debugState} = props

    return {debugState}
  },
)

////////////////////////////////////////////////////////////////////////////////

const DebugToggles = observer(() => {
  const {api} = useStores<{api: WidgetApi}>()

  const overlayModes = values(OverlayMode) as OverlayMode[]
  const flows = values(PaymentFlow) as PaymentFlow[]
  const languages = api.translator.getAvailableLanguages()
  const useCreditCardIframe = api.computedConfig.useCreditCardIframe
  const currentOverlayMode = api.computedConfig.overlayMode
  const currentFlow = api.computedConfig.flow
  const currentLanguage = api.computedConfig.language
  const testMode = api.computedConfig.testMode
  const returnParameters = api.computedConfig.returnParameters
  const debugErrorMessages = api.computedConfig.debugErrorMessages
  const debugSlots = api.computedConfig.debugSlots

  const setOverlayMode = (value) => (api.config.overlayMode = value)
  const setFlow = (value) => (api.config.flow = value)
  const setUseCreditCardIframe = () =>
    (api.config.useCreditCardIframe = !useCreditCardIframe)
  const setLanguage = (value) => (api.config.language = value)
  const setTestMode = () => (api.config.testMode = !testMode)
  const setReturnParameters = () =>
    (api.config.returnParameters = !returnParameters)
  const setDebugErrorMessages = () =>
    (api.config.debugErrorMessages = !debugErrorMessages)
  const setDebugSlots = () => (api.config.debugSlots = !debugSlots)

  return (
    <DebugToggle title="Main" toggle="main">
      <div className="actions">
        <label>
          Language:
          <select
            value={currentLanguage}
            onChange={(e) => setLanguage(e.target.value)}
          >
            {languages.map((language, key) => (
              <option key={key} value={language}>
                {language.toUpperCase()}
              </option>
            ))}
          </select>
        </label>

        <label>
          Overlay mode:
          <select
            value={currentOverlayMode}
            onChange={(e) => setOverlayMode(e.target.value)}
          >
            {overlayModes.map((overlayMode, key) => (
              <option key={key} value={overlayMode}>
                {overlayMode}
              </option>
            ))}
          </select>
        </label>

        <label>
          Flow:
          <select value={currentFlow} onChange={(e) => setFlow(e.target.value)}>
            {flows.map((flow, key) => (
              <option key={key} value={flow}>
                {flow}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="actions">
        <label className="block">
          <input
            type="checkbox"
            checked={testMode}
            onChange={() => setTestMode()}
          />
          Test mode
        </label>

        <label className="block">
          <input
            type="checkbox"
            checked={returnParameters}
            onChange={() => setReturnParameters()}
          />
          Return parameters
        </label>

        <label className="block">
          <input
            type="checkbox"
            checked={useCreditCardIframe}
            onChange={() => setUseCreditCardIframe()}
          />
          Use credit card iframe
        </label>

        <label className="block">
          <input
            type="checkbox"
            checked={debugErrorMessages}
            onChange={() => setDebugErrorMessages()}
          />
          Debug error messages
        </label>

        <label className="block">
          <input
            type="checkbox"
            checked={debugSlots}
            onChange={() => setDebugSlots()}
          />
          Debug slots
        </label>
      </div>
    </DebugToggle>
  )
})

////////////////////////////////////////////////////////////////////////////////

const DebugPaymentForm = observer(() => {
  const {api} = useStores<{api: WidgetApi}>()
  const {debugState} = useDebugState()
  const form = api.paymentForm
  const hideComputations = debugState.hideComputations
  const hideNonComputations = debugState.hideNonComputations
  const setHideComputations = () => {
    debugState.hideComputations = !hideComputations
    debugState.hideNonComputations = false
  }
  const setHideNonComputations = () => {
    debugState.hideNonComputations = !hideNonComputations
    debugState.hideComputations = false
  }

  let paymentConfig = form.getPaymentConfigWithComputations()
  const paymentFormData = toJS(form.data)

  if (hideComputations) {
    paymentConfig = form.getPaymentConfig()
  } else if (hideNonComputations) {
    paymentConfig = form.getPaymentConfigComputations() as any
  }

  const paymentErrors = toJS(form.validationErrors)

  return (
    <DebugToggle title="Payment Form Data" toggle="payment_form">
      <div className="info">
        Payment form data (that is filled out by the user)
      </div>

      <pre>{JSON.stringify(paymentFormData, null, 2)}</pre>

      <div className="info">Payment errors</div>

      <pre>{JSON.stringify(paymentErrors, null, 2)}</pre>

      <div className="info">Payment config (data generated for payment)</div>

      <div className="actions">
        <label>
          <input
            className="mr-1"
            type="checkbox"
            checked={hideComputations}
            onChange={() => setHideComputations()}
          />
          Hide computed fields
        </label>

        <label className="ml-2">
          <input
            className="mr-1"
            type="checkbox"
            checked={hideNonComputations}
            onChange={() => setHideNonComputations()}
          />
          Hide non computed fields
        </label>
      </div>

      <pre>{JSON.stringify(paymentConfig, null, 2)}</pre>
    </DebugToggle>
  )
})

///////////////////////////////////////////////////////////////////////////////

const DebugSubscriptionRenewForm = observer(() => {
  const {api} = useStores<{api: WidgetApi}>()
  const form = api.subscriptionRenewForm
  const subscriptionRenewPaymentConfig = form.getPaymentConfig()
  const subscriptionRenewForm = toJS(form.data)
  const subscriptionRenewErrors = toJS(form.validationErrors)

  return (
    <DebugToggle
      title="Subscription Renew Form"
      toggle="subscription_renew_form"
    >
      <div className="info">
        Subscription renew form (data that is filled out by the user)
      </div>

      <pre>{JSON.stringify(subscriptionRenewForm, null, 2)}</pre>

      <div className="info">Payment errors</div>

      <pre>{JSON.stringify(subscriptionRenewErrors, null, 2)}</pre>

      <div className="info">Payment config (data generated for payment)</div>

      <pre>{JSON.stringify(subscriptionRenewPaymentConfig, null, 2)}</pre>
    </DebugToggle>
  )
})

////////////////////////////////////////////////////////////////////////////////

const DebugTransactionInfo = observer(() => {
  const {api} = useStores<{api: WidgetApi}>()
  const transactionInfo = toJS(api.transactionInfo)

  return (
    <DebugToggle title="Transaction Info" toggle="transaction_info">
      <pre>{JSON.stringify(transactionInfo, null, 2)}</pre>
    </DebugToggle>
  )
})

////////////////////////////////////////////////////////////////////////////////

const DebugSubscriptionInfo = observer(() => {
  const {api} = useStores<{api: WidgetApi}>()
  const subscriptionInfo = toJS(api.subscriptionInfo)

  return (
    <DebugToggle title="Subscription Info" toggle="subscription_info">
      <pre>{JSON.stringify(subscriptionInfo, null, 2)}</pre>
    </DebugToggle>
  )
})

////////////////////////////////////////////////////////////////////////////////

const DebugCustomerInfo = observer(() => {
  const {api} = useStores<{api: WidgetApi}>()
  const customerInfo = toJS(api.customerInfo)

  return (
    <DebugToggle title="Customer Info" toggle="customer_info">
      <pre>{JSON.stringify(customerInfo, null, 2)}</pre>
    </DebugToggle>
  )
})

////////////////////////////////////////////////////////////////////////////////

const DebugEpmsPaymentInfo = observer(() => {
  const {api} = useStores<{api: WidgetApi}>()
  const epmsPaymentInfo = toJS(api.epmsPaymentInfo)

  return (
    <DebugToggle title="EPMS Payment Info" toggle="epms_payment_info">
      <pre>{JSON.stringify(epmsPaymentInfo, null, 2)}</pre>
    </DebugToggle>
  )
})

////////////////////////////////////////////////////////////////////////////////

const DebugEpmsSubscriptionInfo = observer(() => {
  const {api} = useStores<{api: WidgetApi}>()
  const epmsSubscriptionInfo = toJS(api.epmsSubscriptionInfo)

  return (
    <DebugToggle title="EPMS Subscription Info" toggle="epms_subscription_info">
      <pre>{JSON.stringify(epmsSubscriptionInfo, null, 2)}</pre>
    </DebugToggle>
  )
})

////////////////////////////////////////////////////////////////////////////////

const DebugEpmsPaymentSourceInfo = observer(() => {
  const {api} = useStores<{api: WidgetApi}>()
  const epmsPaymentSourceInfo = toJS(api.epmsPaymentSourceInfo)

  return (
    <DebugToggle
      title="EPMS Payment Source Info"
      toggle="epms_payment_source_info"
    >
      <pre>{JSON.stringify(epmsPaymentSourceInfo, null, 2)}</pre>
    </DebugToggle>
  )
})

////////////////////////////////////////////////////////////////////////////////

const DebugEpmsPaymentRequestResponseData = observer(() => {
  const {api} = useStores<{api: WidgetApi}>()
  const epmsPaymentRequestData = toJS(api.epmsPaymentRequestData)
  const epmsPaymentResponseData = toJS(api.epmsPaymentResponseData)
  const epmsPaymentResponseError = toJS(api.epmsPaymentResponseError)

  return (
    <DebugToggle
      title="EPMS Payment request/response"
      toggle="epms_payment_request_response"
    >
      <div className="info">EPMS payment request data</div>
      <pre>{JSON.stringify(epmsPaymentRequestData, null, 2)}</pre>

      <div className="info">EPMS payment response data</div>
      <pre>{JSON.stringify(epmsPaymentResponseData, null, 2)}</pre>

      <div className="info">EPMS payment response error</div>
      <pre>{JSON.stringify(epmsPaymentResponseError, null, 2)}</pre>
    </DebugToggle>
  )
})

////////////////////////////////////////////////////////////////////////////////

const DebugFlags = observer(() => {
  const {api} = useStores<{api: WidgetApi}>()
  const availableFlags = values(WidgetFlag) as WidgetFlag[]

  const isFlagEnabled = (flag) => api.flags.has(flag)
  const toggleFlag = (flag) => api.flags.toggle(flag)

  return (
    <DebugToggle title="Flags" toggle="flags">
      <div className="info">Manually toggle widget flags</div>
      <div className="actions">
        {availableFlags.map((flag, key) => (
          <label key={key} className="block">
            <input
              className="mr-1"
              type="checkbox"
              checked={isFlagEnabled(flag)}
              onChange={() => toggleFlag(flag)}
            />
            {flag}
          </label>
        ))}
      </div>
    </DebugToggle>
  )
})

////////////////////////////////////////////////////////////////////////////////

const DebugConfig = observer(() => {
  const {api} = useStores<{api: WidgetApi}>()
  const runtimeConfig = toJS(api.config)
  const filteredRuntimeConfig = {}
  const loadedConfig = toJS(
    api.translator.getConfigs(api.computedConfig.language),
  )
  const computedConfig = toJS(api.computedConfig)
  computedConfig.translations = '...' as any

  // strip "null" values
  keys(runtimeConfig).forEach((k) => {
    if (runtimeConfig[k] !== null) {
      filteredRuntimeConfig[k] = runtimeConfig[k]
    }
  })

  return (
    <DebugToggle title="Config" toggle="config">
      <div className="info">
        Config that has been provided during widget instantiation
      </div>
      <pre>{JSON.stringify(filteredRuntimeConfig, null, 2)}</pre>

      <div className="info">Config that has been loaded from YAML</div>
      <pre>{JSON.stringify(loadedConfig, null, 2)}</pre>

      <div className="info">Config computed from runtime and loaded config</div>
      <pre>{JSON.stringify(computedConfig, null, 2)}</pre>
    </DebugToggle>
  )
})

////////////////////////////////////////////////////////////////////////////////

const DebugTranslations = observer(() => {
  const {api} = useStores<{api: WidgetApi}>()
  const {debugState} = useDebugState()
  const language = api.computedConfig.language
  const showAllTranslations = debugState.showAllTranslations
  const showTranslationsFallbacks = debugState.showTranslationsFallbacks

  const setShowAllTranslations = () =>
    (debugState.showAllTranslations = !showAllTranslations)
  const setShowTranslationsFallbacks = () =>
    (debugState.showTranslationsFallbacks = !showTranslationsFallbacks)

  let translations = null

  if (showAllTranslations && showTranslationsFallbacks) {
    translations = api.translator.getLoadedTranslationsWithFallbacks()
  } else if (!showAllTranslations && !showTranslationsFallbacks) {
    translations = api.translator.getTranslations(language)
  } else if (showAllTranslations && !showTranslationsFallbacks) {
    translations = api.translator.getLoadedTranslations()
  } else if (!showAllTranslations && showTranslationsFallbacks) {
    translations = api.translator.getTranslationsWithFallbacks(language)
  }

  return (
    <DebugToggle title="Translations" toggle="translations">
      <div className="info">Translations that are in use</div>
      <div className="actions">
        <label>
          <input
            className="mr-1"
            type="checkbox"
            checked={showAllTranslations}
            onChange={() => setShowAllTranslations()}
          />
          &nbsp;Show all languages
        </label>

        <label className="ml-2">
          <input
            type="checkbox"
            checked={showTranslationsFallbacks}
            onChange={() => setShowTranslationsFallbacks()}
          />
          &nbsp;Show fallbacks
        </label>
      </div>

      <pre>{JSON.stringify(translations, null, 2)}</pre>
    </DebugToggle>
  )
})

///////////////////////////////////////////////////////////////////////////////

const DebugBlocks = observer(() => {
  const {api} = useStores<{api: WidgetApi}>()
  const availableWidgetBlocks = values(Block) as Block[]

  const enabledPaymentWidgetBlocks = api.resolveConfig<Block>(
    'paymentWidgetBlocks',
  )

  const isPaymentWidgetBlockEnabled = (block) =>
    enabledPaymentWidgetBlocks.indexOf(block) !== -1

  const togglePaymentWidgetBlock = (block) => {
    if (isPaymentWidgetBlockEnabled(block)) {
      api.config.paymentWidgetBlocks = enabledPaymentWidgetBlocks.filter(
        (b) => b !== block,
      )
    } else {
      enabledPaymentWidgetBlocks.push(block)
      api.config.paymentWidgetBlocks = enabledPaymentWidgetBlocks
    }
  }

  return (
    <DebugToggle title="Blocks" toggle="blocks">
      <div className="info">Donation widget</div>
      <div className="actions">
        {availableWidgetBlocks.map((block, key) => (
          <label key={key} className="block">
            <input
              className="mr-1"
              type="checkbox"
              checked={isPaymentWidgetBlockEnabled(block)}
              onChange={() => togglePaymentWidgetBlock(block)}
            />
            {block}
          </label>
        ))}
      </div>
    </DebugToggle>
  )
})

////////////////////////////////////////////////////////////////////////////////

const DebugConditions = observer(() => {
  const {api} = useStores<{api: WidgetApi}>()
  const {debugState} = useDebugState()
  const form = api.paymentForm
  const currentStatement1 = sprintf(
    'eq(purpose(), %s) and eq(amount(), %s) and eq(paymentMethod(), %s) and eq(paymentType(), %s) and eq(recurringInterval(), %s)',
    form.getPurpose(),
    form.getAmount(),
    form.getPaymentMethod(),
    form.getPaymentType(),
    form.getRecurringInterval(),
  )
  const currentStatement2 = sprintf(
    'purpose() == %s and amount() == %s and paymentMethod() == %s and paymentType() == %s and recurringInterval() == %s',
    form.getPurpose(),
    form.getAmount(),
    form.getPaymentMethod(),
    form.getPaymentType(),
    form.getRecurringInterval(),
  )
  const supportedOperators = api.compiler.getSupportedOperators()
  const supportedMethods = api.compiler.getSupportedMethods()

  const setStatement = (statement) => (debugState.statement = statement)
  const parseStatement = () => {
    if (debugState.statement == '') {
      return (debugState.statementResult = '')
    }

    try {
      const result = api.compiler.compile(debugState.statement)
      debugState.statementResult = JSON.stringify(result, null, 2)
    } catch (err) {
      console.error(err)
      debugState.statementResult = 'error'
    }
  }

  return (
    <DebugToggle title="Conditions" toggle="conditions">
      <div className="info">
        Conditions are dynamic expressions that are computed at runtime
      </div>

      <div className="subtitle">Tester</div>
      <div className="info">
        Try out your own condition statements in the box below:
      </div>

      <div className="tester">
        <div className="expression">
          <input
            type="text"
            className="flex-auto border px-2 text-xs rounded-l"
            value={debugState.statement}
            onChange={(e) => setStatement(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && parseStatement()}
            placeholder="Statement..."
          />
          <button type="button" onClick={() => parseStatement()}>
            parse
          </button>
        </div>

        <div className="result">
          Result:
          <pre>{debugState.statementResult}</pre>
        </div>
      </div>

      <hr />

      <div className="subtitle">Current</div>
      <div className="info">
        Below is a condition consisting of all known lookups
      </div>

      <pre className="wrap">
        {currentStatement1}
        <br />
        // or
        <br />
        {currentStatement2}
      </pre>

      <hr />

      <div className="subtitle">Examples</div>
      <div className="info">
        Copy paste any of the examples below into the tester
      </div>

      <div className="example">
        <div className="info">
          Amount &gt; 20 and payment method either dd or elv
        </div>
        <pre className="wrap">
          <span>gt(amount(), 20) and in(paymentMethod(), [dd,elv])</span>
          <br />
          // or <br />
          <span>amount() &gt; 20 and paymentMethod() in [dd,elv]</span>
        </pre>
      </div>

      <div className="example">
        <div className="info">Computed value with format</div>
        <pre className="wrap">
          fmt("%s = %s", amount(), paymentMethod())
          <br />
          // or <br />
          amount() + " = " + paymentMethod()
        </pre>
      </div>

      <div className="example">
        <div className="info">Computed value without format</div>
        <pre className="wrap">
          fmt(amount(), " = ", paymentMethod())
          <br />
          // or <br />
          amount() + " = " + paymentMethod()
        </pre>
      </div>

      <div className="example">
        <div className="info">Escapes in computed values</div>
        <pre className="wrap">
          fmt(amount(), " \\in\\ \"", paymentMethod(), "\"") <br />
          // or <br />
          fmt("%s \\in\\ \"%s\"", amount(), paymentMethod()) <br />
          // or <br />
          amount() + " \\in\\ \"" + paymentMethod() + "\"") <br />
        </pre>
      </div>

      <hr />

      <div className="subtitle">Vocabulary</div>
      <div className="info">Operators</div>
      <pre className="wrap">{supportedOperators.join(', ')}</pre>

      <div className="info">Methods</div>
      <pre className="wrap">{supportedMethods.join(', ')}</pre>
    </DebugToggle>
  )
})

////////////////////////////////////////////////////////////////////////////////

type DebugToggleProps = {
  open?: boolean
  title?: string
  toggle: string
}

const DebugToggle: FC<DebugToggleProps> = observer((props) => {
  const {api} = useStores<{api: WidgetApi}>()
  const {debugState} = useDebugState()
  const {title, toggle, children, open} = props
  const {uiTransitionTimeout: timeout} = api.computedConfig
  const slideTransitionProps = buildSlideTransitionProps({timeout})
  const show = debugState.toggles.indexOf(toggle) !== -1 || open
  const onClick = () => {
    if (show) {
      debugState.toggles = debugState.toggles.filter((t) => t !== toggle)
    } else {
      debugState.toggles.push(toggle)
    }
  }

  return (
    <div className="section">
      {title && (
        <button type="button" className="header" onClick={onClick}>
          <span className="text">{title}</span>
          <span className="icon">{show ? '\u2212' : '\u002B'}</span>
        </button>
      )}

      <Transition
        in={show}
        mountOnEnter
        unmountOnExit
        {...slideTransitionProps}
      >
        <div className="content">{children}</div>
      </Transition>
    </div>
  )
})
